import { motion } from "framer-motion";
import { Loader2, Pencil, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../services/api"; // ✅ Smart API import

const MyIssuesPage = () => {
  const [issues, setIssues] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchMyIssues = async () => {
    try {
      const res = await API.get("/issues/dashboard/user");
      setIssues(res.data.issues || []);
    } catch (err) {
      console.error("Failed to fetch issues:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyIssues();
  }, []);

  const handleDelete = async (id) => {
    const confirm = window.confirm("Are you sure you want to delete this issue?");
    if (!confirm) return;
    try {
      await API.delete(`/issues/${id}`);
      fetchMyIssues();
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const filteredIssues = issues.filter((issue) => {
    if (filter === "All") return true;
    return issue.status === filter;
  });

  const statusColor = {
    Resolved: "text-green-600 bg-green-50",
    "Under Review": "text-yellow-700 bg-yellow-50",
    Open: "text-blue-700 bg-blue-50",
    Flagged: "text-red-600 bg-red-50",
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 via-white to-green-50 py-10 px-4">
      <div className="max-w-5xl mx-auto">
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-3xl font-bold text-center text-orange-600 mb-6"
        >
          🧾 My Raised Issues
        </motion.h1>

        {/* ✅ Filter Buttons */}
        <div className="flex justify-center flex-wrap gap-3 mb-6">
          {["All", "Open", "Under Review", "Resolved", "Flagged"].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-full font-semibold border transition ${
                filter === status
                  ? "bg-green-600 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-100"
              }`}
            >
              {status}
            </button>
          ))}
        </div>

        {/* ✅ Issue Cards */}
        {loading ? (
          <div className="flex justify-center items-center h-40">
            <Loader2 className="animate-spin text-green-600 w-8 h-8" />
          </div>
        ) : filteredIssues.length === 0 ? (
          <p className="text-center text-gray-500">You haven't raised any issues yet.</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {filteredIssues.map((issue, idx) => (
              <motion.div
                key={idx}
                whileHover={{ scale: 1.01 }}
                transition={{ duration: 0.3 }}
                className="bg-white shadow-md rounded-xl border p-4 space-y-2"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">{issue.title}</h2>
                    <p className="text-sm text-gray-600">{issue.description}</p>
                    <p className="text-sm text-gray-500">
                      📍 {issue.district || "Unknown"}, {issue.constituency || "Unknown"}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-2 items-center">
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                          statusColor[issue.status] || "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {issue.status || "Open"}
                      </span>
                      {issue.aiFlag && (
                        <span className="inline-block bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded">
                          ⚠️ {issue.moderationLabel || "Flagged"}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* ✅ Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/edit-issue/${issue._id}`)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <Pencil size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(issue._id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyIssuesPage;
